import { UserRound } from 'lucide-react';
import ToggleSwitch from '../BaseComponents/Toggle/ToggleSwitch';
import { SECTION, moveItem } from './siteHeaderModel';
import { ACCOUNT_MENU_DEFAULTS, WEBSITE_DEFAULTS } from './headerDefaults';
import { BilingualInput, LinkField, RowActions, SectionCard, SwitchRow } from './editorControls';
import styles from './HeaderEditor.module.css';

const MENU_ITEM_NAMES = {
  bookings: 'My bookings',
  profile: 'Profile',
  favourites: 'Favourites',
};

/**
 * The account entry. Guests see a "Sign in / Register" pill; signed-in visitors see their name
 * and a small account menu. Switching the block off hides the guest pill only: a signed-in
 * visitor always keeps the menu, or they could never sign out.
 */
export default function AccountSection({ value, onPatch, errorCount }) {
  const a = value;
  const reg = a.register;
  const items = a.menuItems || [];
  const patchItem = (key, p) => onPatch({ menuItems: items.map((m) => (m.key === key ? { ...m, ...p } : m)) });

  return (
    <SectionCard
      id={SECTION.account}
      icon={UserRound}
      tone="dark"
      title="Account"
      description="Sign in and register for guests, the account menu for signed-in visitors."
      enabled={a.enabled}
      onToggle={(enabled) => onPatch({ enabled })}
      showOn={a.showOn}
      onShowOn={(showOn) => onPatch({ showOn })}
      errorCount={errorCount}
      previewTarget="account"
    >
      <p className={styles.note}>
        Off hides the sign in / register entry for guests. Signed-in visitors always keep their account
        menu, so they can still reach their bookings and sign out.
      </p>

      <h4 className={styles.subTitle}>Guests</h4>
      <div className={styles.grid2}>
        <BilingualInput
          label="Sign in text"
          value={a.signInLabel}
          defaults={WEBSITE_DEFAULTS.signIn}
          onChange={(signInLabel) => onPatch({ signInLabel })}
        />
        <LinkField
          label="Sign in link"
          value={a.signInUrl}
          placeholder="/login"
          errorPath="account.signInUrl"
          hint="Empty opens /login."
          onChange={(signInUrl) => onPatch({ signInUrl })}
        />
      </div>

      <SwitchRow
        title="Register"
        description="The second half of the pill. Off leaves Sign in alone."
        checked={reg.enabled}
        onChange={(enabled) => onPatch({ register: { ...reg, enabled } })}
      />
      {reg.enabled && (
        <div className={styles.grid2}>
          <BilingualInput
            label="Register text"
            value={reg.label}
            defaults={WEBSITE_DEFAULTS.register}
            onChange={(label) => onPatch({ register: { ...reg, label } })}
          />
          <LinkField
            label="Register link"
            value={reg.url}
            placeholder="/register"
            errorPath="account.register.url"
            hint="Empty opens /register."
            onChange={(url) => onPatch({ register: { ...reg, url } })}
          />
        </div>
      )}

      <h4 className={styles.subTitle}>Signed-in visitors</h4>
      <BilingualInput
        label="Name when no first name is known"
        value={a.fallbackName}
        defaults={WEBSITE_DEFAULTS.myAccount}
        onChange={(fallbackName) => onPatch({ fallbackName })}
      />

      <div className={styles.field}>
        <label className={styles.fieldLabel}>Account menu</label>
        <div className={styles.itemList}>
          {items.map((m, index) => (
            <div key={m.key} className={`${styles.compactRow} ${m.enabled ? '' : styles.itemCardOff}`}>
              <span className={styles.orderIndex}>{index + 1}</span>
              <strong className={styles.compactName}>{MENU_ITEM_NAMES[m.key]}</strong>
              <div className={styles.compactField}>
                <BilingualInput
                  value={m.label}
                  defaults={ACCOUNT_MENU_DEFAULTS[m.key]}
                  onChange={(label) => patchItem(m.key, { label })}
                  compact
                />
              </div>
              <span className={styles.toggleWrap} title={m.enabled ? 'Shown in the account menu' : 'Hidden from the account menu'}>
                <ToggleSwitch checked={m.enabled} onChange={() => patchItem(m.key, { enabled: !m.enabled })} />
              </span>
              <RowActions
                index={index}
                count={items.length}
                onMove={(i, dir) => onPatch({ menuItems: moveItem(items, i, dir) })}
              />
            </div>
          ))}
          <span className={styles.hint}>Sign out is always the last entry.</span>
        </div>
      </div>
    </SectionCard>
  );
}
